import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { animate, motion, useMotionValue, useTransform } from 'framer-motion';

import { GlassCard } from '../../../../components/glass';
import { brl } from './format';

interface KpiCardProps {
  label: string;
  valor: number;
  qtd?: number;
  color: string;
  icon?: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
  index?: number;
  /** Toggle-tip (InfoPopover) explicando como o valor é calculado. */
  info?: React.ReactNode;
  /** 'currency' (default) formata em R$; 'number' contagem; 'percent' 0-100%. */
  format?: 'currency' | 'number' | 'percent';
  /** Variação vs período anterior. `good` = a direção é positiva para esta métrica. */
  delta?: { pct: number; good: boolean };
}

/**
 * Número com count-up (framer-motion): anima de 0 → valor no mount e, quando o
 * filtro muda, conta suave do valor antigo para o novo (sinaliza "recalculou").
 */
const fmtKpi = (v: number, format: 'currency' | 'number' | 'percent') => {
  if (format === 'number') return Math.round(v).toLocaleString('pt-BR');
  if (format === 'percent') return `${v.toFixed(1).replace('.', ',')}%`;
  return brl(v);
};

const KpiValue = ({
  valor,
  format,
}: {
  valor: number;
  format: 'currency' | 'number' | 'percent';
}) => {
  const mv = useMotionValue(0);
  const display = useTransform(mv, v => fmtKpi(v, format));
  React.useEffect(() => {
    const controls = animate(mv, valor, { duration: 0.6, ease: 'easeOut' });
    return () => controls.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valor]);
  return <motion.span>{display}</motion.span>;
};

export const KpiCard = ({
  label,
  valor,
  qtd,
  color,
  icon,
  active,
  onClick,
  index = 0,
  info,
  format = 'currency',
  delta,
}: KpiCardProps) => {
  // Valor longo (ex: R$ 5.559.628,40) encolhe pra caber sem quebrar no meio do
  // número — em coluna estreita (hub com 6 KPIs) quebrava "R$ 5.559.62 / 8,40".
  const fmtLen = fmtKpi(valor, format).length;
  let valueSize = '17px';
  if (fmtLen <= 12) valueSize = '22px';
  else if (fmtLen <= 15) valueSize = '19px';
  // Δ% com base minúscula explode (ex: ▲685850%); acima de 999% vira "999%+".
  const pctAbs = delta ? Math.min(Math.abs(delta.pct), 999) : 0;
  const pctEstourou = !!delta && Math.abs(delta.pct) > 999;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: 0.05 * index }}
    >
      <GlassCard
        intensity="medium"
        hoverable={!!onClick}
        p={5}
        h="full"
        cursor={onClick ? 'pointer' : 'default'}
        onClick={onClick}
        borderColor={active ? color : undefined}
        boxShadow={active ? `0 0 0 2px ${color}` : undefined}
        role={onClick ? 'button' : undefined}
        aria-pressed={onClick ? !!active : undefined}
      >
        <Flex justify="space-between" align="start" gap={2}>
          <Flex align="center" gap={1}>
            <Text
              fontSize="xs"
              fontWeight={600}
              color="#71717A"
              textTransform="uppercase"
              letterSpacing="0.04em"
            >
              {label}
            </Text>
            {info}
          </Flex>
          {icon && <Box color={color}>{icon}</Box>}
        </Flex>
        <Text
          fontSize={valueSize}
          fontWeight={700}
          color="#0F172A"
          mt={2}
          lineHeight="1.2"
          whiteSpace="nowrap"
          className="tabular-nums"
        >
          <KpiValue valor={valor} format={format} />
        </Text>
        {delta && (
          <Flex align="center" gap={1} mt={1.5}>
            <Text fontSize="11px" fontWeight={700} color={delta.good ? '#16A34A' : '#DC2626'}>
              {delta.pct >= 0 ? '▲' : '▼'} {pctAbs.toFixed(0)}%{pctEstourou ? '+' : ''}
            </Text>
            <Text fontSize="11px" color="#94A3B8">
              vs. anterior
            </Text>
          </Flex>
        )}
        {qtd !== undefined && (
          <Text fontSize="xs" color="#94A3B8" mt={1}>
            {qtd} {qtd === 1 ? 'título' : 'títulos'}
          </Text>
        )}
      </GlassCard>
    </motion.div>
  );
};
